import { useCallback, useRef } from 'react';

type LoadMoreQuery = {
    hasNextPage: boolean;
    fetchNextPage: () => void;
    // explicit root to observe against, for callers that already hold a ref to their scrollable container.
    // omit (or pass null) to auto-detect via findScrollableAncestor -
    // needed for callers like a modal body rendered by a third-party component, which expose no ref of their own
    root?: Element | null;
};

// with a null/viewport root, a sentinel clipped by a small fixed-height scrollable ancestor (e.g. a modal body)
// can still count as "intersecting" the page viewport, firing immediately instead of when the user actually
// scrolls to it — so we walk up to the nearest scrollable ancestor and use that as the root instead
const findScrollableAncestor = (node: HTMLElement): Element | null => {
    let el = node.parentElement;
    while (el) {
        const { overflowY } = window.getComputedStyle(el);
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return el;
        }
        el = el.parentElement;
    }
    return null;
};

export const useLoadMoreObserver = (query: LoadMoreQuery) => {
    // holds the latest query without making the ref callback below depend on it: `query` is a fresh object on
    // every caller render, and a ref callback re-runs (detach + reattach) whenever its identity changes - so
    // depending on `query` would tear down and recreate the observer on every render, not just on real
    // mount/unmount. IntersectionObserver always reports current state immediately on `.observe()`, so that churn
    // re-triggers the "did I just start observing" check on every render, which can cascade into fetching every
    // page in a row instead of only when the user actually scrolls
    const queryRef = useRef(query);
    queryRef.current = query;

    const observerRef = useRef<IntersectionObserver | null>(null);

    // a ref callback (rather than a plain ref + effect) so the observer is (re)created exactly when the sentinel
    // node actually mounts/unmounts, even if that happens on a later commit than this hook's own render (e.g. a
    // portal-rendered modal), where a useEffect here would otherwise read a still-null ref and never fire again
    const observerElem = useCallback((node: HTMLDivElement | null) => {
        observerRef.current?.disconnect();

        if (!node) {
            return;
        }

        // IntersectionObserver always invokes the callback once immediately on observe(), reporting whatever the
        // sentinel's intersection happens to be at that exact instant. If the page's own rows are still individually
        // loading their own data (e.g. each row's own name/athletes fetch) the list can be sparse for a moment,
        // making the sentinel appear visible before real content has settled - so we ignore that first, unconditional
        // report and only act on later ones, which reflect genuine scrolling or layout changes.
        let isInitialReport = true;

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                if (isInitialReport) {
                    isInitialReport = false;
                    return;
                }
                if (entry.isIntersecting && queryRef.current.hasNextPage) {
                    queryRef.current.fetchNextPage();
                }
            },
            { root: queryRef.current.root ?? findScrollableAncestor(node), threshold: 0 },
        );
        observerRef.current.observe(node);
    }, []);

    return { observerElem };
};
